/**
 * [T-066] Side conversations — คุยข้างเคียงในการ์ด (ถามคนนอก ลูกค้าไม่เห็น) · tasks/W7-CONTRACTS.md §3
 * อีเมลขาออกผ่าน internalMailer stream `notification` · Reply-To = `side+s<convId ไม่มีขีด>.<kid>.<sig8>@<DEFAULT_MAIL_DOMAIN>`
 * ขาเข้า: verify ผ่าน → เข้าเธรดเดิมแล้วจบ (ห้ามสร้างการ์ดใหม่) · verify ไม่ผ่าน → ทางเดิมของ inbound ปกติ
 */
import { z } from "zod";
import { Attachment } from "./attachments.ts";

export const SideChannel = z.enum(["email", "telegram"]);
export type SideChannel = z.infer<typeof SideChannel>;

export const SideStatus = z.enum(["open", "closed"]);
export type SideStatus = z.infer<typeof SideStatus>;

export const SideParticipant = z.object({
  address: z.email(),
  name: z.string().nullable(),
  role: z.enum(["to", "cc"]),
});
export type SideParticipant = z.infer<typeof SideParticipant>;

/** R1 — เปิดคุยข้างเคียง (channel email เท่านั้นใน T-066) */
export const SideConversationCreate = z.object({
  channel: z.literal("email"),
  subject: z.string().trim().min(1).max(998),
  to: z.array(z.email()).min(1).max(20),
  cc: z.array(z.email()).max(20).default([]),
  body_html: z.string().min(1).max(500_000),
});
export type SideConversationCreate = z.infer<typeof SideConversationCreate>;

/** R1 — ตอบในเธรดเดิม (ไม่ระบุ to/cc = ผู้รับเดิมของเธรด) */
export const SideMessageCreate = z.object({
  body_html: z.string().min(1).max(500_000),
  to: z.array(z.email()).min(1).max(20).optional(),
  cc: z.array(z.email()).max(20).optional(),
});
export type SideMessageCreate = z.infer<typeof SideMessageCreate>;

/** R1 — คัดลอกข้อความที่เลือกเข้าเป็นโน้ตภายในของการ์ด */
export const SideToNoteInput = z.object({
  message_ids: z.array(z.uuid()).min(1).max(50),
});
export type SideToNoteInput = z.infer<typeof SideToNoteInput>;

export const SideMessage = z.object({
  id: z.uuid(),
  direction: z.enum(["inbound", "outbound"]),
  author_user_id: z.uuid().nullable(),
  from_address: z.string(),
  to: z.array(z.string()),
  subject: z.string(),
  body_html: z.string(),
  body_text: z.string(),
  attachments: z.array(z.uuid()),
  /** Staff-only metadata; URLs are minted on demand after checking current note access. */
  attachment_details: z.array(Attachment).optional(),
  outbox_message_id: z.string().nullable(),
  created_at: z.iso.datetime(),
});
export type SideMessage = z.infer<typeof SideMessage>;

export const SideConversation = z.object({
  id: z.uuid(),
  ticket_id: z.uuid(),
  ticket_public_id: z.string(),
  channel: SideChannel,
  subject: z.string(),
  status: SideStatus,
  participants: z.array(SideParticipant),
  created_by: z.uuid(),
  created_by_name: z.string().nullable(),
  /** ที่อยู่ตอบกลับ `side+…@<domain>` ของเธรดนี้ (email) — null เมื่อ telegram */
  reply_to: z.string().nullable(),
  created_at: z.iso.datetime(),
  last_message_at: z.iso.datetime(),
  closed_at: z.iso.datetime().nullable(),
  closed_by: z.uuid().nullable(),
  /** ข้อความล่าสุดตามลำดับเวลา (จำกัด 50 ต่อเธรด — R1) */
  messages: z.array(SideMessage),
});
export type SideConversation = z.infer<typeof SideConversation>;

export const SideConversationList = z.object({ items: z.array(SideConversation) });
export type SideConversationList = z.infer<typeof SideConversationList>;

/** Minimal recipients for staff with note access to a ticket. */
export const SideRecipient = z.object({
  email: z.email(), name: z.string(), kind: z.enum(["staff", "team"]),
  team: z.string(), department: z.string(),
});
export type SideRecipient = z.infer<typeof SideRecipient>;
export const SideRecipientList = z.object({ items: z.array(SideRecipient) });
export type SideRecipientList = z.infer<typeof SideRecipientList>;
